import { ablyChannelNamesForSession } from "_shared/_stores/realtime/ably-channel-names";
import reduxAblyMiddleware, { withCallbacks } from "_shared/_stores/realtime/redux-ably-middleware";
import { RootState } from "_shared/_stores/root-reducer";
import { AppDispatch } from "_shared/_stores/store-types";
import { calendarEventListTag, calendarEventsApi } from "calendar/_api/calendar-event-api";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import { documentsApi } from "documents/_api/document-api";
import { permissionsApi, sessionPermissionTag } from "permissions/_api/permissions-api-service";
import Session from "users/__session/session";
import { userInformationApi } from "users/information/_api/user-information-api";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import { ProposalStatus } from "work/proposals/__constants/proposal-lifecycle";
import { proposalsApi } from "work/proposals/_api/proposal-api";
import ProposalSchema, { type ProposalJSON } from "work/proposals/_schemas/proposal-schema";
import { proposalReviewerStateHasChanged } from "work/proposals/_store/proposal-server-state";
import {
  updateServerProposal,
  recordLatestRevisionRedirect,
  recordProposalNoLongerAvailable,
  removeProposal,
  replaceOriginalProposalBuilder,
  replaceUpdatedProposalBuilder,
} from "work/proposals/_store/proposals-redux-slice";
import Proposal from "work/proposals/proposal";
import {
  proposalCollaborationApi,
  proposalCollaborationTagId,
} from "work/proposals/negotiation/proposal-collaboration-api";
import {
  allProposalCollaborationsResyncRequested,
  proposalResyncRequested,
} from "work/proposals/negotiation/proposal-collaboration-redux-slice";
import {
  parseProposalCollaborationRealtimeNotice,
  PROPOSAL_COLLABORATION_REALTIME_EVENT_NAME,
} from "work/proposals/negotiation/proposal-collaboration-realtime";

interface ProposalDeletedEvent {
  id: string;
}

interface OnboardingAccessInvalidatedEvent {
  userId: string;
  legalEntityId: string;
  requestId: string;
  proposalIds?: string[];
}

const parseOnboardingAccessInvalidatedEvent = (payload: unknown): OnboardingAccessInvalidatedEvent => {
  if (!payload || typeof payload !== "object") throw new Error("Invalid onboarding access invalidation payload");
  const value = payload as Record<string, unknown>;
  if (["userId", "legalEntityId", "requestId"].some((key) => typeof value[key] !== "string")) {
    throw new Error("Onboarding access invalidation requires identifier fields");
  }
  if (Object.keys(value).some((key) => !["userId", "legalEntityId", "requestId", "proposalIds"].includes(key))) {
    throw new Error("Onboarding access invalidation cannot carry proposal data");
  }
  const proposalIds = value.proposalIds;
  if (proposalIds !== undefined && (!Array.isArray(proposalIds) || proposalIds.some((id) => typeof id !== "string"))) {
    throw new Error("Onboarding access invalidation proposal IDs must be strings");
  }
  return {
    userId: value.userId as string,
    legalEntityId: value.legalEntityId as string,
    requestId: value.requestId as string,
    proposalIds: proposalIds as string[] | undefined,
  };
};

type PartialProposalUpdate = Partial<
  Pick<ProposalJSON, "lastUpdated" | "latestRevisionId" | "supersededById" | "partyRevision">
>;

const partialProposalKeys = ["latestRevisionId", "supersededById", "partyRevision"] as const satisfies readonly (
  keyof ProposalJSON
)[];
const allowedPartialProposalUpdateKeys = new Set<string>([
  "id",
  "isUnauthorized",
  "lastUpdated",
  ...partialProposalKeys,
]);

const getPartialProposalUpdate = (payload: unknown): PartialProposalUpdate | undefined => {
  const payloadRecord = payload as Record<string, unknown>;
  const hasPartialProposalUpdate = partialProposalKeys.some((key) => key in payloadRecord);
  if (!hasPartialProposalUpdate) return undefined;

  const update: PartialProposalUpdate = {};
  if ("lastUpdated" in payloadRecord) update.lastUpdated = payloadRecord.lastUpdated as ProposalJSON["lastUpdated"];
  if ("latestRevisionId" in payloadRecord) {
    update.latestRevisionId = payloadRecord.latestRevisionId as ProposalJSON["latestRevisionId"];
  }
  if ("supersededById" in payloadRecord) {
    update.supersededById = payloadRecord.supersededById as ProposalJSON["supersededById"];
  }
  if ("partyRevision" in payloadRecord) {
    update.partyRevision = payloadRecord.partyRevision as ProposalJSON["partyRevision"];
  }

  return update;
};

const isPartialProposalUpdate = (payload: unknown): payload is { id: string } & PartialProposalUpdate => {
  const payloadRecord = payload as Record<string, unknown> | null | undefined;
  if (!payloadRecord || typeof payloadRecord !== "object") return false;
  if (typeof payloadRecord.id !== "string") return false;
  return (
    partialProposalKeys.some((key) => key in payloadRecord) &&
    Object.keys(payloadRecord).every((key) => allowedPartialProposalUpdateKeys.has(key))
  );
};

const getSupersededProposalIdValue = (proposalJSON: ProposalJSON): string | undefined =>
  proposalJSON.supersedes?.id !== proposalJSON.id ? (proposalJSON.supersedes?.id ?? undefined) : undefined;

const getPreviousLatestRevisionIdValue = (proposalJSON: ProposalJSON): string | undefined => {
  if (proposalJSON.previousLatestRevisionId) {
    return proposalJSON.previousLatestRevisionId === proposalJSON.id
      ? undefined
      : proposalJSON.previousLatestRevisionId;
  }

  return [proposalJSON.supersedes?.latestRevisionId, proposalJSON.supersedes?.id].find(
    (idValue): idValue is string => Boolean(idValue) && idValue !== proposalJSON.id,
  );
};

const recordLatestRevisionRedirectFromProposal = ({
  dispatch,
  incomingProposalJSON,
}: {
  dispatch: AppDispatch;
  incomingProposalJSON: ProposalJSON;
}) => {
  if (!incomingProposalJSON.id) return;

  const supersededProposalIdValue = getSupersededProposalIdValue(incomingProposalJSON);
  const previousLatestRevisionId = getPreviousLatestRevisionIdValue(incomingProposalJSON);
  if (!previousLatestRevisionId) return;

  dispatch(
    recordLatestRevisionRedirect({
      previousLatestRevisionId,
      latestRevisionId: incomingProposalJSON.id,
      supersededProposalId: supersededProposalIdValue,
    }),
  );
};

const recordLatestRevisionRedirectFromPartialUpdate = ({
  dispatch,
  partialProposalUpdate,
  proposalIdValue,
}: {
  dispatch: AppDispatch;
  partialProposalUpdate: PartialProposalUpdate;
  proposalIdValue: string;
}) => {
  const latestRevisionId = partialProposalUpdate.latestRevisionId ?? partialProposalUpdate.supersededById;
  if (!latestRevisionId || latestRevisionId === proposalIdValue) return;

  dispatch(
    recordLatestRevisionRedirect({
      previousLatestRevisionId: proposalIdValue,
      latestRevisionId,
      supersededProposalId: partialProposalUpdate.supersededById === latestRevisionId ? proposalIdValue : undefined,
    }),
  );
};

const invalidateProposalIndexes = (dispatch: AppDispatch) => {
  dispatch(
    proposalsApi.util.invalidateTags([
      { type: "Proposal", id: "LIST" },
      { type: "Proposal", id: "DraftCount" },
      { type: "Proposal", id: "ActiveCount" },
      { type: "Proposal", id: "ArchiveCount" },
      { type: "Proposal", id: "InboxCount" },
    ]),
  );
};

const invalidateCalendarEvents = (dispatch: AppDispatch) => {
  dispatch(calendarEventsApi.util.invalidateTags([calendarEventListTag]));
};

const upsertProposalById = ({
  dispatch,
  proposalJSON,
  proposalIdValue,
  session,
}: {
  dispatch: AppDispatch;
  proposalJSON: ProposalJSON | null;
  proposalIdValue: string;
  session: Session;
}) => {
  dispatch(
    proposalsApi.util.upsertQueryData("getProposalById", { proposalId: proposalIdValue, session }, proposalJSON),
  );
};

const invalidateProposalById = (dispatch: AppDispatch, proposalIdValue: string) => {
  dispatch(proposalsApi.util.invalidateTags([{ type: "Proposal", id: proposalIdValue }]));
};

const updateProposalByIdCache = ({
  dispatch,
  proposalJSON,
  proposalIdValue,
  session,
}: {
  dispatch: AppDispatch;
  proposalJSON: ProposalJSON;
  proposalIdValue: string;
  session: Session;
}) => {
  upsertProposalById({ dispatch, proposalJSON, proposalIdValue, session });
  dispatch(updateServerProposal(proposalJSON));
};

const handleUnauthorizedProposal = ({
  dispatch,
  proposalIdValue,
  session,
}: {
  dispatch: AppDispatch;
  proposalIdValue: string;
  session: Session;
}) => {
  upsertProposalById({ dispatch, proposalJSON: null, proposalIdValue, session });
  dispatch(removeProposal(new Guid(proposalIdValue)));
  // Losing access while inside the proposal is the same experience as it being deleted: the viewer must
  // be told and sent back rather than left editing something they can no longer save.
  dispatch(recordProposalNoLongerAvailable(new Guid(proposalIdValue)));
};

const callbacks = withCallbacks<RootState, AppDispatch>()
  .add(PROPOSAL_COLLABORATION_REALTIME_EVENT_NAME, (payload: unknown) => (dispatch, getState) => {
    const notice = parseProposalCollaborationRealtimeNotice(payload);
    const currentVersion = getState().proposalCollaboration.byNegotiationId[notice.negotiationId]?.envelope?.streamVersion;
    // Redux and RTK Query are separate caches. A command response (or another mounted proposal-id
    // query) can advance the Redux envelope before this particular page query has adopted the same
    // viewer-specific capabilities. Never use the Redux cursor to suppress query invalidation: an
    // equal/duplicate notice must still make the mounted page refetch the canonical envelope. The
    // cursor guard is useful only for the Redux resync marker itself.
    if (currentVersion === undefined || currentVersion < notice.streamVersion) {
      dispatch(proposalResyncRequested({ negotiationId: notice.negotiationId }));
    }
    dispatch(
      proposalCollaborationApi.util.invalidateTags([
        { type: "Proposal", id: proposalCollaborationTagId(notice.proposalId) },
        // A submission may change the projection's proposal id. The mounted
        // query can still be keyed by the previous id, so invalidate the shared
        // collaboration tag as well to make realtime identical to refresh.
        { type: "Proposal", id: "COLLABORATION" },
      ]),
    );
    // The canonical notice is also the source of truth for proposal indexes.
    // A submission can move a row between Draft and Active, or replace the
    // current proposal id, without any legacy proposal-created/updated event.
    invalidateProposalIndexes(dispatch);
  })
  .add("onboarding-access-invalidated", (payload: unknown) => (dispatch) => {
    const notice = parseOnboardingAccessInvalidatedEvent(payload);
    dispatch(userInformationApi.util.invalidateTags([{ type: "UserInformation", id: notice.userId }]));
    dispatch(permissionsApi.util.invalidateTags([sessionPermissionTag]));
    invalidateProposalIndexes(dispatch);
    for (const proposalId of notice.proposalIds ?? []) {
      invalidateProposalById(dispatch, proposalId);
      dispatch(
        proposalCollaborationApi.util.invalidateTags([
          { type: "Proposal", id: proposalCollaborationTagId(proposalId) },
        ]),
      );
    }
    dispatch(allProposalCollaborationsResyncRequested());
  })
  .add("proposal-created", (payload: unknown) => (dispatch, getState) => {
    invalidateCalendarEvents(dispatch);
    const incomingProposalIdValue = (payload as { id: string } | null | undefined)?.id;
    const isUnauthorized = (payload as { isUnauthorized: boolean } | null | undefined)?.isUnauthorized ?? false;
    if (isUnauthorized) {
      if (!incomingProposalIdValue) {
        throw new Error("Received proposal-created event with no proposal ID");
      }
      const session = Session.loadFromStorage(() => {});
      handleUnauthorizedProposal({ dispatch, proposalIdValue: incomingProposalIdValue, session });
      invalidateProposalIndexes(dispatch);
      return;
    }

    const incomingProposalJSON = ProposalSchema.validateJSON(payload);
    if (!incomingProposalJSON.id) {
      throw new Error("Received proposal-created event with no proposal ID");
    }
    const session = Session.loadFromStorage(() => {});
    const state = getState();
    const existingProposal = state.proposals.byId[incomingProposalJSON.id]?.serverVersion;
    if (existingProposal) {
      const incomingLastUpdated = AHDate.fromJSON(incomingProposalJSON.lastUpdated);
      const existingLastUpdated = AHDate.fromJSON(existingProposal.lastUpdated);
      if (existingLastUpdated && incomingLastUpdated && existingLastUpdated.isAfter(incomingLastUpdated)) {
        return; // ignore stale create
      }
    }
    updateProposalByIdCache({
      dispatch,
      proposalIdValue: incomingProposalJSON.id,
      proposalJSON: incomingProposalJSON,
      session,
    });
    invalidateProposalIndexes(dispatch);
    recordLatestRevisionRedirectFromProposal({ dispatch, incomingProposalJSON });
  })
  .add("proposal-updated", (payload: unknown) => (dispatch, getState) => {
    invalidateCalendarEvents(dispatch);
    const incomingProposalIdValue = (payload as { id: string } | null | undefined)?.id;
    if (!incomingProposalIdValue) {
      throw new Error("Received proposal-updated event with no proposal ID");
    }
    const session = Session.loadFromStorage(() => {});

    invalidateProposalIndexes(dispatch);

    const state = getState();
    const existingProposal = state.proposals.byId[incomingProposalIdValue]?.serverVersion;
    const incomingProposalLastUpdated = AHDate.fromJSON((payload as { lastUpdated?: string }).lastUpdated);
    const existingLastUpdated = AHDate.fromJSON(existingProposal?.lastUpdated) ?? undefined;
    if (incomingProposalLastUpdated && existingLastUpdated?.isAfter(incomingProposalLastUpdated)) {
      return; // ignore stale update
    }
    const incomingServerVersionIsNewer = Boolean(
      incomingProposalLastUpdated && (!existingLastUpdated || incomingProposalLastUpdated.isAfter(existingLastUpdated)),
    );
    const isUnauthorized = (payload as { isUnauthorized: boolean } | null | undefined)?.isUnauthorized ?? false;
    if (isUnauthorized) {
      handleUnauthorizedProposal({ dispatch, proposalIdValue: incomingProposalIdValue, session });
      return;
    }

    const partialProposalUpdate = getPartialProposalUpdate(payload);
    if (existingProposal && partialProposalUpdate && isPartialProposalUpdate(payload)) {
      updateProposalByIdCache({
        dispatch,
        proposalIdValue: incomingProposalIdValue,
        proposalJSON: {
          ...existingProposal,
          ...partialProposalUpdate,
        },
        session,
      });
      recordLatestRevisionRedirectFromPartialUpdate({
        dispatch,
        partialProposalUpdate,
        proposalIdValue: incomingProposalIdValue,
      });
      return;
    }

    let incomingProposalJSON;
    try {
      incomingProposalJSON = ProposalSchema.validateJSON(payload);
    } catch {
      if (existingProposal && partialProposalUpdate) {
        updateProposalByIdCache({
          dispatch,
          proposalIdValue: incomingProposalIdValue,
          proposalJSON: {
            ...existingProposal,
            ...partialProposalUpdate,
          },
          session,
        });
        recordLatestRevisionRedirectFromPartialUpdate({
          dispatch,
          partialProposalUpdate,
          proposalIdValue: incomingProposalIdValue,
        });
        return;
      }

      invalidateProposalById(dispatch, incomingProposalIdValue);
      return;
    }

    const authoritativeReviewerStateChanged = proposalReviewerStateHasChanged(existingProposal, incomingProposalJSON);

    updateProposalByIdCache({
      dispatch,
      proposalIdValue: incomingProposalIdValue,
      proposalJSON: incomingProposalJSON,
      session,
    });
    if (incomingProposalJSON.status === ProposalStatus.Archived) {
      dispatch(documentsApi.util.invalidateTags([{ type: "Document", id: "LIST" }]));
    }
    recordLatestRevisionRedirectFromProposal({ dispatch, incomingProposalJSON });

    const incomingProposal = Proposal.fromJSON({
      json: incomingProposalJSON,
      currentUser: session.user!,
      options: { withRedline: true },
    });
    if (!incomingProposal?.id || incomingProposal?.isSubmitted) return;

    const existingOriginalBuilderJSON = state.proposals.byId[incomingProposal.id.value]?.draftBuilder?.original;
    const existingOriginalBuilder = ProposalBuilder.fromJSON({
      json: existingOriginalBuilderJSON,
      session,
    });

    const existingUpdatedBuilderJSON = state.proposals.byId[incomingProposal.id.value]?.draftBuilder?.updated;
    const existingUpdatedBuilder = ProposalBuilder.fromJSON({
      json: existingUpdatedBuilderJSON,
      session,
    });

    let originalBuilder = existingOriginalBuilder;
    let updatedBuilder = existingUpdatedBuilder;
    if (!updatedBuilder) {
      return;
    }
    if (!originalBuilder) {
      console.warn("Original proposal builder missing, setting to updated builder", {
        proposalId: incomingProposal.id.value,
      });
      originalBuilder = updatedBuilder;
    }

    // Builder timestamps are generated in the browser and change whenever a local
    // builder is cloned. They are not comparable to the server's proposal
    // timestamp: a reviewer can have a newer-looking local builder while still
    // needing an authoritative reviewer/edit-lock update from the server.
    // Compare server versions instead. Reviewer state is also compared directly
    // so records written before reviewer mutations advanced lastUpdated still
    // rebase, while a genuinely equal Ably echo leaves local work alone.
    if (incomingServerVersionIsNewer || authoritativeReviewerStateChanged) {
      originalBuilder = new ProposalBuilder({
        session,
        startingSpec: incomingProposal.spec,
      });
      updatedBuilder = updatedBuilder.mergeUpdatedSpec(incomingProposal.spec);
    }
    dispatch(
      replaceOriginalProposalBuilder({
        proposalId: new Guid(incomingProposalIdValue),
        builder: originalBuilder,
      }),
    );
    dispatch(
      replaceUpdatedProposalBuilder({
        proposalId: new Guid(incomingProposalIdValue),
        builder: updatedBuilder,
      }),
    );
  })
  .add("proposal-deleted", (payload: unknown) => (dispatch) => {
    invalidateCalendarEvents(dispatch);
    const incomingProposalIdValue = (payload as ProposalDeletedEvent)?.id;
    if (!incomingProposalIdValue) {
      throw new Error("Received proposal-updated event with no proposal ID");
    }
    const session = Session.loadFromStorage(() => {});
    invalidateProposalIndexes(dispatch);
    dispatch(
      proposalsApi.util.upsertQueryData("getProposalById", { proposalId: incomingProposalIdValue, session }, null),
    );

    dispatch(
      replaceUpdatedProposalBuilder({
        proposalId: new Guid(incomingProposalIdValue),
        builder: undefined,
      }),
    );
    dispatch(removeProposal(new Guid(incomingProposalIdValue)));
    // The durable "this is gone" fact an open editor watches. Clearing the caches above is not enough
    // on its own: the editor renders from the canonical negotiation envelope, which keeps its last good
    // projection, so without this a reviewer went on editing a proposal that no longer exists.
    dispatch(recordProposalNoLongerAvailable(new Guid(incomingProposalIdValue)));
  });

export const proposalsAblyReduxMiddleware = reduxAblyMiddleware<RootState, AppDispatch>({
  callbacks,
  channelName: ablyChannelNamesForSession.proposals,
  connectionHealth: { key: "proposals", label: "Proposals" },
  onContinuityLost: (dispatch) => {
    dispatch(allProposalCollaborationsResyncRequested());
    dispatch(proposalCollaborationApi.util.invalidateTags([{ type: "Proposal", id: "COLLABORATION" }]));
  },
  shouldConnectionStartImmediately: true,
});
